import {
  Analytics,
  AnalyticsTrackEvent,
} from '@porsche-code/kado-analytics-client';
import {
  NotAuthorized,
  NotSignedIn,
  SignInForm,
} from '@porsche-code/kado-auth';
import { Spinner, UnexpectedError } from '@porsche-code/kado-ui';
import { Outlet } from '@tanstack/react-router';
import { ClientError } from 'graphql-request';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import {
  PersonActionKind,
  Rule,
  useAbilityContext,
  usePersonContext,
} from '@/context';
import { useMeQuery } from '@/graphql';

import { EmailNotificationsHint } from './EmailNotificationsHint';
import { UserDeletionConsent } from './UserDeletionConsent';

const IS_PASSWORD_FLOW_ACTIVE =
  process.env.REACT_APP_IS_PASSWORD_FLOW_ACTIVE === 'true';

const UNKNOWN_ROLE_ERROR_MESSAGE = 'Trying to use unknown role';

export const SignIn = ({ allowAnonymous }: { allowAnonymous?: boolean }) => {
  const { state: ability } = useAbilityContext();
  const { dispatch: dispatchPerson, state: personState } = usePersonContext();
  const {
    t,
    i18n: { changeLanguage, language },
  } = useTranslation(NAMESPACES);

  const {
    data: person,
    isFetching,
    isSuccess,
    isError,
    error: personError,
  } = useMeQuery({ isAuthenticated: personState.isAuthenticated } as never, {
    enabled: personState.isAuthenticated,
    refetchOnWindowFocus: false,
    retry: (failureCount, error: Error) => {
      if (error.message.includes(UNKNOWN_ROLE_ERROR_MESSAGE)) {
        return false;
      }

      if (error instanceof ClientError) {
        const { status } = error.response;

        if (status >= 400 && status < 500) {
          // Do not retry client-side errors (4xx), except 401 (auth errors)
          return status === 401 && failureCount < 3;
        }
      }

      return failureCount < 3;
    },
    select: (data) => data.me,
  });

  useEffect(() => {
    if (isSuccess && !!person && !personState.isAuthorized) {
      dispatchPerson({
        type: PersonActionKind.Authorize,
        payload: person,
      });

      // Change language if it is not the default language.
      const locale = 'settings' in person ? person.settings?.locale : null;
      if (locale && language !== locale) {
        changeLanguage(locale);
      }

      ability.update(person.ability.dashboard as Rule[]);

      if (person.__typename === 'Person') {
        Analytics.identify({
          id: person.id,
          organizationId: person.organization.parentMarket?.organizationId,
        });
      }

      Analytics.track(AnalyticsTrackEvent.Login);
    }
  }, [
    isSuccess,
    person,
    dispatchPerson,
    language,
    ability,
    changeLanguage,
    personState.isAuthorized,
  ]);

  switch (true) {
    case isFetching:
    case personState.isInitializing:
    case isSuccess && !!person && !personState.isAuthorized:
      return <Spinner data-testid="loading-spinner" size="2xl" />;
    case isError && !personError.message.includes(UNKNOWN_ROLE_ERROR_MESSAGE):
      return (
        <UnexpectedError
          heading={t('common:unexpectedError.title')}
          description={t('common:unexpectedError.text')}
        />
      );
    case (personState.isAuthenticated && !personState.isAuthorized) ||
      (!allowAnonymous && personState.isAnonymous):
      return <NotAuthorized />;
    case personState.isAuthenticated && personState.isAuthorized:
      return (
        <>
          <Outlet />
          <EmailNotificationsHint />
          <UserDeletionConsent />
        </>
      );
    case IS_PASSWORD_FLOW_ACTIVE:
      return <SignInForm />;
    default:
      return <NotSignedIn />;
  }
};
